import type { CSSProperties, ReactNode } from 'react';
import { IconArrowDown, IconArrowUp, IconChevronLeft, IconChevronRight, IconChevronsUpDown } from '../icons';
import { formatNumber } from '../i18n';
import { useTranslate } from '../i18n/react';

/**
 * A table that only draws.
 *
 * <p>Fetching, filtering, sorting and paging stay in the screen. That line is what stops this
 * becoming the component every screen has to fight, and it is why the definition screens can
 * share it with the question library without either bending.</p>
 */
export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  width?: CSSProperties['width'];
  /** `is-muted` for secondary text, `is-primary` for the column the screen is about. */
  className?: string;
  align?: 'left' | 'right';
  sortable?: boolean;
  /** Width of the shimmer bar in this column while loading. */
  skeletonWidth?: string;
}

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  columnId: string;
  direction: SortDirection;
}
export type TableStatus = 'loading' | 'error' | 'ready';

export interface TableProps<T> {
  /** Read out before the table. Not decoration — it is the only thing that says what this is. */
  caption: string;
  columns: Column<T>[];
  rows: readonly T[];
  getRowId: (row: T) => string;
  status: TableStatus;
  sort?: SortState | null;
  onSortChange?: (next: SortState) => void;
  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  /** An extra class for one row — `is-draft` for the one being edited. */
  rowClassName?: (row: T) => string | undefined;
  /** Rendered above the rows, for the row being added. */
  leadingRow?: ReactNode;
  emptyState: ReactNode;
  errorState?: ReactNode;
  skeletonRows?: number;
}

const classes = (...parts: Array<string | null | undefined | false>) => parts.filter(Boolean).join(' ') || undefined;

export function Table<T>({
  caption,
  columns,
  rows,
  getRowId,
  status,
  sort,
  onSortChange,
  onRowClick,
  rowActions,
  rowClassName,
  leadingRow,
  emptyState,
  errorState,
  skeletonRows = 8,
}: TableProps<T>) {
  const t = useTranslate();
  const colCount = columns.length + (rowActions ? 1 : 0);

  const ariaSort = (column: Column<T>) => {
    if (!column.sortable) return undefined;
    if (sort?.columnId !== column.id) return 'none' as const;
    return sort.direction === 'asc' ? ('ascending' as const) : ('descending' as const);
  };

  const nextSort = (column: Column<T>): SortState => ({
    columnId: column.id,
    direction: sort?.columnId === column.id && sort.direction === 'asc' ? 'desc' : 'asc',
  });

  return (
    <div className="nv-table-scroll">
      <table className="nv-table">
        <caption className="nv-sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => {
              const SortIcon =
                sort?.columnId === column.id
                  ? sort.direction === 'asc'
                    ? IconArrowUp
                    : IconArrowDown
                  : IconChevronsUpDown;

              return (
                <th
                  key={column.id}
                  scope="col"
                  style={{ width: column.width }}
                  className={classes(column.sortable && 'is-sortable', column.align === 'right' && 'is-numeric')}
                  aria-sort={ariaSort(column)}
                >
                  {column.sortable && onSortChange ? (
                    <button type="button" onClick={() => onSortChange(nextSort(column))}>
                      {column.header}
                      <SortIcon size={13} />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
            {rowActions ? (
              <th scope="col" className="nv-actions-col">
                <span className="nv-sr-only">{t('common.actions')}</span>
              </th>
            ) : null}
          </tr>
        </thead>

        <tbody aria-busy={status === 'loading' || undefined}>
          {leadingRow}

          {status === 'loading'
            ? Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={`skeleton-${i}`} aria-hidden="true">
                  {columns.map((column) => (
                    <td key={column.id}>
                      <div className="nv-skeleton" style={{ height: 12, width: column.skeletonWidth ?? '70%' }} />
                    </td>
                  ))}
                  {rowActions ? <td /> : null}
                </tr>
              ))
            : null}

          {status === 'error' && errorState ? (
            <tr>
              <td colSpan={colCount} className="nv-state-cell">{errorState}</td>
            </tr>
          ) : null}

          {status === 'ready' && rows.length === 0 && !leadingRow ? (
            <tr>
              <td colSpan={colCount} className="nv-state-cell">{emptyState}</td>
            </tr>
          ) : null}

          {status === 'ready'
            ? rows.map((row) => (
                <tr
                  key={getRowId(row)}
                  className={classes(onRowClick && 'is-clickable', rowClassName?.(row))}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={classes(column.className, column.align === 'right' && 'is-numeric')}
                      // Read by the phone layout, where there is no header row to look up at.
                      data-label={typeof column.header === 'string' ? column.header : undefined}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                  {rowActions ? (
                    <td className="nv-actions-col" onClick={(e) => e.stopPropagation()}>
                      <div className="nv-row-actions">{rowActions(row)}</div>
                    </td>
                  ) : null}
                </tr>
              ))
            : null}
        </tbody>
      </table>
    </div>
  );
}

export interface TableFooterProps {
  total: ReactNode;
  /** 1-based. */
  page: number;
  pageSize: number;
  rowCount: number;
  pageSizeOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

/** How many there are, how many fit on a page, and where in them you are. */
export function TableFooter({
  total,
  page,
  pageSize,
  rowCount,
  pageSizeOptions = [25, 50, 100],
  onPageChange,
  onPageSizeChange,
}: TableFooterProps) {
  const t = useTranslate();
  const pageCount = Math.max(1, Math.ceil(rowCount / pageSize));
  const pages = [...new Set([1, page - 1, page, page + 1, pageCount])]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);

  return (
    <footer className="nv-table-footer">
      <span>{total}</span>

      <div className="nv-table-footer__right">
        <label className="nv-page-size">
          {t('common.pageSize')}
          <select className="nv-select" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>

        <nav className="nv-pagination" aria-label={t('common.pagination')}>
          <button
            type="button"
            className="nv-page"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={t('common.previousPage')}
          >
            <IconChevronLeft size={14} />
          </button>

          {pages.map((p, i) => (
            <span key={p} className="nv-page-slot">
              {i > 0 && p - (pages[i - 1] ?? p) > 1 ? <span aria-hidden="true">&hellip;</span> : null}
              <button
                type="button"
                className="nv-page"
                aria-current={p === page ? 'page' : undefined}
                onClick={() => onPageChange(p)}
              >
                {formatNumber(p)}
              </button>
            </span>
          ))}

          <button
            type="button"
            className="nv-page"
            disabled={page >= pageCount}
            onClick={() => onPageChange(page + 1)}
            aria-label={t('common.nextPage')}
          >
            <IconChevronRight size={14} />
          </button>
        </nav>
      </div>
    </footer>
  );
}
